import { useCallback } from 'react';

import {
    useReportsController_CreateReport,
    useReportsController_DeleteReport,
    useReportsController_Report,
    useReportsController_ReportList,
} from '@/services/hooks';
import type { NewReport, ReportDetail } from '@/services/types';

import {
    type DomainMutation,
    type DomainMutationCallbacks,
    ignoreBody,
    toMutationOptions,
    useMappedMutation,
} from '../shared/mutation';
import { type DomainQuery, useMappedQuery } from '../shared/query';

import type { HarvestReportListItem } from './types';

function body<T>(value: T): T {
    return value;
}

/** The pen's harvest reports, drafts included. */
export function useGetHarvestReports(cageId: number): DomainQuery<HarvestReportListItem[]> {
    return useMappedQuery(useReportsController_ReportList(cageId), body<HarvestReportListItem[]>);
}

/** One harvest report with its distributions; a 404 means it does not exist (any more). */
export function useGetHarvestReport(cageId: number, reportId: number): DomainQuery<ReportDetail> {
    return useMappedQuery(useReportsController_Report(cageId, reportId), body<ReportDetail>);
}

export function useCreateHarvestReport(
    cageId: number,
    callbacks?: DomainMutationCallbacks
): DomainMutation<NewReport, void> {
    const toApi = useCallback((report: NewReport) => ({ cageId, requestBody: report }), [cageId]);
    return useMappedMutation(
        useReportsController_CreateReport(toMutationOptions(callbacks, ignoreBody)),
        toApi,
        ignoreBody
    );
}

export function useDeleteHarvestReport(
    cageId: number,
    callbacks?: DomainMutationCallbacks
): DomainMutation<number, void> {
    const toApi = useCallback((reportId: number) => ({ cageId, reportId }), [cageId]);
    return useMappedMutation(
        useReportsController_DeleteReport(toMutationOptions(callbacks, ignoreBody)),
        toApi,
        ignoreBody
    );
}
